import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { TemplateDownload } from '../templateDownload';

type UploadGuidanceProps = {
    /** The row limit, formatted for the copy. */
    limit: string;
    isPreparingTemplate: boolean;
    templateFailed: boolean;
    onDownloadTemplate: () => void;
};

type GuidanceStepProps = {
    number: number;
    title: string;
    children: React.ReactNode;
};

/** One numbered step; the list itself carries the order for assistive tech. */
const GuidanceStep: React.FC<GuidanceStepProps> = ({
    number,
    title,
    children,
}) => (
    <li className="flex gap-4">
        <Chip
            aria-hidden
            className="shrink-0 self-start"
            color="default"
            variant="soft"
        >
            {number}
        </Chip>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Typography weight="semibold">{title}</Typography>
            {children}
        </div>
    </li>
);

/**
 * The three steps beside the drop zone: download the template, fill it in,
 * upload it. The first holds the Download template button.
 */
export const UploadGuidance: React.FC<UploadGuidanceProps> = ({
    limit,
    isPreparingTemplate,
    templateFailed,
    onDownloadTemplate,
}) => {
    const { t } = useTranslation('pages');
    const fillRules = [
        t('catalogImport.upload.guide.fill.rows'),
        t('catalogImport.upload.guide.fill.phones'),
        t('catalogImport.upload.guide.fill.limit', { limit }),
        t('catalogImport.upload.guide.fill.category'),
    ];

    return (
        <ol
            className="flex flex-col gap-7"
            {...getNodeTestIdProp('catalog-import-upload-guide')}
        >
            <GuidanceStep
                number={1}
                title={t('catalogImport.upload.guide.download.title')}
            >
                <TemplateDownload
                    hasFailed={templateFailed}
                    isPreparing={isPreparingTemplate}
                    onDownload={onDownloadTemplate}
                />
            </GuidanceStep>
            <GuidanceStep
                number={2}
                title={t('catalogImport.upload.guide.fill.title')}
            >
                <ul className="flex list-disc flex-col gap-1 ps-5">
                    {fillRules.map((rule) => (
                        <li key={rule}>
                            <Typography color="muted" type="body-sm">
                                {rule}
                            </Typography>
                        </li>
                    ))}
                </ul>
            </GuidanceStep>
            <GuidanceStep
                number={3}
                title={t('catalogImport.upload.guide.upload.title')}
            >
                <Typography color="muted" type="body-sm">
                    {t('catalogImport.upload.guide.upload.body')}
                </Typography>
            </GuidanceStep>
        </ol>
    );
};
